import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";

type Size = "sm" | "md" | "lg" | "xl";

const SIZE: Record<Size, string> = {
  sm: "size-6 text-[13px]",
  md: "size-8 text-[17px]",
  lg: "size-10 text-[22px]",
  xl: "size-14 text-[30px]",
};

/**
 * Compact Grantonomy G-square: black G on a pale yellow rounded square.
 * Kept for API stability; not used by GrantonomyLogo / BrandLockup.
 */
export function GLogo({
  size = "md",
  variant = "yellow",
  className,
}: {
  size?: Size;
  variant?: "yellow" | "black";
  className?: string;
}) {
  const isYellow = variant === "yellow";
  return (
    <span
      aria-hidden
      className={cn(
        "grid shrink-0 place-items-center rounded-[6px] font-display font-extrabold leading-none",
        SIZE[size],
        isYellow ? "bg-yellow text-black" : "bg-black text-white",
        className,
      )}
    >
      G
    </span>
  );
}

/** Host Grotesk “Grantonomy” wordmark. Kept for API stability; not used by GrantonomyLogo / BrandLockup. */
export function GWordmark({ className }: { className?: string }) {
  return (
    <span className={cn("font-display font-extrabold tracking-tight text-foreground", className)}>
      Grantonomy
    </span>
  );
}

/** Official Grantonomy lockup (yellow G-square + wordmark) as a single SVG image. */
export function GrantonomyLogo({ className }: { className?: string }) {
  return (
    <img src="/grantonomy-logo.svg" alt="Grantonomy" className={cn("h-20 w-auto", className)} />
  );
}

/** Sidebar lockup: official SVG plus i18n tagline flush with the G-square’s left edge. */
export function BrandLockup({ className }: { className?: string }) {
  const { t } = useTranslation();
  return (
    <span className={cn("flex flex-col items-start leading-tight", className)}>
      <GrantonomyLogo className="h-12" />
      <span className="mt-1 text-sm font-medium text-muted-foreground">{t("brand.tagline")}</span>
    </span>
  );
}
